import { useState } from "react";

import { uniqueId } from "lodash";
import { useForm } from "react-hook-form";

import { RoomType, type Building, type Floor, type PostBuildingRoomsRequestBody, type Room } from "@eisox/api/core-api";
import { toast, ActionButton, Modal, Plan } from "@eisox/design-system";
import { PencilIcon, RoomIcon } from "@eisox/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { roomApi } from "~/api";
import { ConfirmCancelPopup } from "~/components/confirm-cancel-popup";
import ExpandedRoom from "~/components/expended-room";
import { DEFAULT_COMFORT_TEMPERATURE, DEFAULT_NIGHT_TEMPERATURE } from "~/constants/building.constant";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { m } from "~/paraglide/messages";
import type { RoomGroup } from "~/types/room.type";

import { reOrderItemsUid } from "../helpers";
import {
  checkGetName,
  prefixId,
  resolveRoomKey,
  roomFormToCreateBody,
  schemaErrors,
  type RoomFormDto,
  type RoomsDto,
} from "../helpers/add-room.helper";
import { AddRoomForm } from "./add-room-form";

interface AddRoomDialogProps extends Omit<Modal.RootProps, "children"> {
  building: Building;
  floorId: string;
  floors: Floor[];
  rooms: Room[];
  roomsGroups: RoomGroup[];
}

export const AddRoomDialog: React.FC<AddRoomDialogProps> = ({
  building,
  floorId,
  floors,
  rooms,
  roomsGroups,
  open,
  onOpenChange,
  ...props
}) => {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    meta: { toast: { action: "create" } },
    mutationFn: (body: PostBuildingRoomsRequestBody) => roomApi.create(building.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries(roomApi.queries.building(building.id));
      handleOpenChange(false);
    },
  });

  /* GLOBAL FORM */
  const {
    formState: { isDirty, errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm<RoomsDto>({
    values: {},
  });

  const globalRooms = watch(); // {[key: string]: RoomFormDto[]}
  const fields = Object.values(globalRooms).flat(); // RoomFormDto[]

  /* ROOMS FORM */
  const initialRoom = {
    id: uniqueId(prefixId),
    comfortTemperature: DEFAULT_COMFORT_TEMPERATURE,
    nightTemperature: DEFAULT_NIGHT_TEMPERATURE,
    isSwitchEnabled: false,
    floor: { id: floorId },
    groupNames: [],
    type: RoomType.OFFICE,
    pattern: "(niveau)_(type)_(index)",
    uid: 1,
    name: "",
  };
  const formRoom = useForm<RoomFormDto>({
    defaultValues: {
      ...initialRoom,
      name: checkGetName(floors, initialRoom),
    },
    resolver: zodResolver(
      schemaErrors([...rooms.map(r => r.name), ...fields.map(r => r.name)], 1, globalRooms, floors),
    ),
  });

  const currentRoom: RoomFormDto = formRoom.watch();
  const isCurrentRoomIsOnPlan = !!Object.values(fields).find(f => f.id === currentRoom.id);

  const { data: plan } = useQuery({ ...floorPlanQueries.byId(currentRoom.floor.id), staleTime: 0 });

  const [confirmCancelPopup, setConfirmCancelPopup] = useState(false);

  const onSubmit = handleSubmit(data => {
    mutate(Object.values(data).flat().map(roomFormToCreateBody));
  });

  const append = (data: RoomFormDto) => {
    const key = resolveRoomKey(floors, data);
    const existing = globalRooms[key] ?? [];
    const value = [...existing, data];
    setValue(key, value, { shouldDirty: true });
  };

  const setToNextRoom = (room: RoomFormDto) => {
    const uid = room.uid != null ? room.uid + 1 : null;
    formRoom.reset({
      ...room,
      id: uniqueId(prefixId),
      floor: { id: room.floor.id },
      uid,
      name: checkGetName(floors, { ...room, uid }),
    });
  };

  const handleAddRoom = (data: RoomFormDto) => {
    append(data);
    setToNextRoom(data);
  };

  const handleUpdateRoom = (data: RoomFormDto) => {
    const currentKey = Object.keys(globalRooms).find(key => globalRooms[key]?.some(f => f.id === data.id));
    const newKey = resolveRoomKey(floors, data);
    const isChangePattern =
      !!(formRoom.formState.dirtyFields.pattern && data.pattern) ||
      !!formRoom.formState.dirtyFields.floor?.id ||
      !!formRoom.formState.dirtyFields.type;
    const oldRoom = fields.find(f => f.id === data.id);
    let startUid = oldRoom?.uid;
    const targetUid = data.uid;

    if (currentKey && currentKey !== newKey) {
      const currentArray = globalRooms[currentKey];
      if (currentArray) {
        const removedRoom = currentArray.find(f => f.id === data.id);
        const oldArray = currentArray.filter(f => f.id !== data.id);
        const reindexedArray = oldArray.map(f => {
          if (data.pattern && f.uid && removedRoom?.uid && f.uid > removedRoom.uid) {
            const newUid = f.uid - 1;
            return { ...f, name: checkGetName(floors, { ...f, uid: newUid }), uid: newUid };
          }
          return f;
        });
        setValue(currentKey, reindexedArray, { shouldDirty: true });
      }
    }

    let array: RoomFormDto[] = globalRooms[newKey] ?? [];
    const index = currentKey === newKey ? array.findIndex(f => f.id === data.id) : -1;

    if (index !== -1 && !isChangePattern) {
      const existingRoom = array[index];
      if (existingRoom) {
        array[index] = { ...data, uid: existingRoom.uid };
      }
    } else if (array.length > 0 && formRoom.formState.dirtyFields.uid && isChangePattern) {
      const lastUid = array.at(-1)?.uid;
      startUid = lastUid != null ? lastUid + 1 : null;
      array = [...array, { ...data, uid: startUid }];
    } else array = [...array, data];

    if (formRoom.formState.dirtyFields.uid && targetUid && startUid && array.length > 0) {
      const orderUid = reOrderItemsUid(array, startUid, targetUid);
      if (orderUid) {
        array = orderUid.map(f => ({ ...f, name: checkGetName(floors, f) }));
      }
    }
    setValue(newKey, array, { shouldDirty: true });
    const lastRoom = array.at(-1);
    if (lastRoom) setToNextRoom(lastRoom);
  };

  const deleteRoom = (data: RoomFormDto) => {
    const array = globalRooms[resolveRoomKey(floors, data)];
    if (!array) return [];
    const index = array.findIndex(f => f.id === data.id);
    if (index === -1) return array;
    const newArray = [...array.slice(0, index), ...array.slice(index + 1)];
    const deletedRoom = array[index];
    const deletedUid = deletedRoom?.uid;
    const updatedArray = newArray.map(f => {
      if (data.pattern && f.uid != null && deletedUid != null && f.uid > deletedUid) {
        const newUid = f.uid - 1;
        return { ...f, name: checkGetName(floors, { ...f, uid: newUid }), uid: newUid };
      }
      return f;
    });
    setValue(data.pattern ? checkGetName(floors, data, false) : "default", updatedArray, { shouldDirty: true });
    return updatedArray;
  };

  const handleDeleteRoom = (data: RoomFormDto) => {
    const array = deleteRoom(data);
    const lastRoom = array.at(-1);
    if (lastRoom) setToNextRoom(lastRoom);
  };

  const handleClickOnPlan = async (point: { x: number; y: number }) => {
    if (isCurrentRoomIsOnPlan) {
      if (formRoom.formState.isDirty) toast.warning(m.add_room_warn_change_but_have_modifications());
      else {
        const lastField = fields.at(-1);
        if (lastField) setToNextRoom(lastField);
      }
    } else {
      formRoom.setValue("floor", { ...currentRoom.floor, x: point.x, y: point.y }, { shouldDirty: true });
      await formRoom.handleSubmit(handleAddRoom)();
    }
  };

  const handleOnChange = (item: RoomFormDto, position: { x: number; y: number }) => {
    const f = fields.find(f => f.id === item.id);
    if (f && (f.floor.x !== position.x || f.floor.y !== position.y)) {
      const key = f.pattern ? checkGetName(floors, f, false) : "default";
      const array = globalRooms[key];
      if (!array) return;
      const index = array.findIndex(r => r.id === f.id);
      if (index !== -1) {
        const existingRoom = array[index];
        if (existingRoom) {
          array[index] = { ...existingRoom, floor: { ...existingRoom.floor, x: position.x, y: position.y } };
          setValue(key, array, { shouldDirty: true });
        }
      }
    }
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
  };

  return (
    <Modal.Root {...props} open={open} onOpenChange={handleOpenChange}>
      <Modal.Content className="flex h-[85dvh] w-[95vw] flex-col">
        <form onSubmit={e => void onSubmit(e)}>
          <Modal.Header title={m.add_rooms()} subtitle={m.add_room_subtitle()} icon={<RoomIcon />}>
            <ActionButton
              variant="cancel"
              onClick={e => {
                e.preventDefault();
                if (isDirty) setConfirmCancelPopup(true);
                else onOpenChange?.(false);
              }}
            >
              {m.cancel()}
            </ActionButton>
            <ActionButton rounded disabled={!isDirty || Object.keys(errors).length > 0} type="submit">
              {m.save()}
              <PencilIcon />
            </ActionButton>
          </Modal.Header>
        </form>
        <div className="gap-10 flex h-full overflow-auto">
          <AddRoomForm
            isCurrentRoomIsOnPlan={isCurrentRoomIsOnPlan}
            className="flex-3"
            form={formRoom}
            handleUpdateRoom={handleUpdateRoom}
            handleSetToNextRoom={() => {
              const lastField = fields.at(-1);
              if (lastField) setToNextRoom(lastField);
            }}
            handleDeleteRoom={handleDeleteRoom}
            fields={globalRooms}
            floors={floors}
            building={building}
            groups={roomsGroups}
          />
          <div className="relative flex-7">
            <Plan.Root
              onClickOnPlan={point => {
                void handleClickOnPlan(point);
              }}
            >
              <Plan.Controls className="top-2.5 left-2.5 absolute z-1" />
              <Plan.Wrapper className="h-full! w-full!" plan={plan}>
                {rooms
                  .filter(r => r.floor.id === currentRoom.floor.id)
                  .map(room => (
                    <Plan.Item
                      key={room.id}
                      asChild
                      defaultPosition={{ x: room.floor.x, y: room.floor.y }}
                      origin="bottom center"
                    >
                      <ExpandedRoom disabled>{room.name}</ExpandedRoom>
                    </Plan.Item>
                  ))}
                {fields
                  .filter(f => f.floor.id === currentRoom.floor.id)
                  .map(field => (
                    <Plan.Item
                      key={field.id}
                      asChild
                      defaultPosition={{ x: field.floor.x, y: field.floor.y }}
                      origin="bottom center"
                      onClick={() => field.id !== currentRoom.id && formRoom.reset(field)}
                      draggable={formRoom.formState.isDirty ? field.id === currentRoom.id : true}
                      onPositionEnd={({ x, y }) => handleOnChange(field, { x, y })}
                    >
                      <ExpandedRoom
                        disabled={isCurrentRoomIsOnPlan && formRoom.formState.isDirty && field.id !== currentRoom.id}
                      >
                        {field.name}
                      </ExpandedRoom>
                    </Plan.Item>
                  ))}
              </Plan.Wrapper>
            </Plan.Root>
          </div>
        </div>

        <ConfirmCancelPopup
          open={confirmCancelPopup}
          onClose={() => setConfirmCancelPopup(false)}
          onValidate={() => onOpenChange?.(false)}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
